import { Section, Title, Button } from './Styled';
import { theme } from '../theme';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import React, { useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const Contact: React.FC = () => {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [captchaState, setCaptchaState] = useState<"loading" | "ready" | "expired" | "error">("loading");
  const { language } = useLanguage();
  const t = translations.contact[language];
  const form = useRef<HTMLFormElement>(null);

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.current || captchaState !== "ready") return;
    setStatus("sending");

    const data = new FormData(form.current);
    data.append('access_key', '679aaad1-052b-4269-8a4e-4b932f8edba7');

    try {
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: data });
      const json = await res.json();
      if (json.success) {
        setStatus("sent");
        form.current.reset();
        setCaptchaState("loading");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };
  
  return (
    <Section id="contact" bg={theme.colors.black}>
      <Title>{t.title}</Title>
      {status === "sent" && (
        <div style={{ textAlign: 'center', marginBottom: '1rem', color: '#31EC56', fontWeight: 600, fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)' }}>
          {t.statusSent}
        </div>
      )}
      {status === "error" && (
        <div style={{ textAlign: 'center', marginBottom: '1rem', color: '#ff4d4f', fontWeight: 600, fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)' }}>
          {t.statusError}
        </div>
      )}
      <form
        ref={form}
        onSubmit={sendEmail}
        style={{
          maxWidth: 'min(420px, calc(100vw - 2rem))',
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.2rem',
          background: '#22272A',
          padding: 'clamp(1rem, 4vw, 2rem)',
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(24,26,27,0.06)',
          border: '1px solid #23282F',
          boxSizing: 'border-box',
        }}
      >
        <input 
          name="name"
          type="text" 
          placeholder={t.name} 
          required 
          style={{ 
            padding: 'clamp(0.7rem, 2vw, 0.8rem)', 
            borderRadius: 6, 
            border: '1px solid #D9D9D9', 
            fontSize: 'max(16px, 1rem)', // Prevents zoom on iOS
            width: '100%',
            boxSizing: 'border-box',
            minHeight: '44px', // Better touch target for mobile
            WebkitAppearance: 'none', // Removes default iOS styling
          }} 
        />
        <input 
          name="email"
          type="email" 
          placeholder={t.email} 
          required 
          style={{ 
            padding: 'clamp(0.7rem, 2vw, 0.8rem)', 
            borderRadius: 6, 
            border: '1px solid #D9D9D9', 
            fontSize: 'max(16px, 1rem)', // Prevents zoom on iOS
            width: '100%',
            boxSizing: 'border-box',
            minHeight: '44px', // Better touch target for mobile
            WebkitAppearance: 'none', // Removes default iOS styling
          }} 
        />
        <input
          name="phone"
          type="tel"
          placeholder={t.phone}
          style={{
            padding: 'clamp(0.7rem, 2vw, 0.8rem)',
            borderRadius: 6,
            border: '1px solid #D9D9D9',
            fontSize: 'max(16px, 1rem)',
            width: '100%',
            boxSizing: 'border-box',
            minHeight: '44px',
            WebkitAppearance: 'none',
          }}
        />
        <textarea
          name="message"
          placeholder={t.message} 
          rows={5} 
          required 
          style={{ 
            padding: 'clamp(0.7rem, 2vw, 0.8rem)', 
            borderRadius: 6, 
            border: '1px solid #D9D9D9', 
            fontSize: 'max(16px, 1rem)', // Prevents zoom on iOS
            width: '100%',
            boxSizing: 'border-box',
            resize: 'vertical',
            minHeight: '120px',
            WebkitAppearance: 'none', // Removes default iOS styling
          }} 
        />
        <Button type="submit" disabled={status === "sending" || captchaState !== "ready"}>
          {status === "sending" ? t.statusSending : captchaState === "loading" ? t.statusVerifying : t.send}
        </Button>
      </form>
      <div style={{ maxWidth: 'min(420px, calc(100vw - 2rem))', width: '100%', margin: '0.8rem auto 0' }}>
        <Turnstile
          siteKey="0x4AAAAAADQCg3Ak8crFi3Jp"
          onSuccess={() => setCaptchaState("ready")}
          onExpire={() => setCaptchaState("expired")}
          onError={() => setCaptchaState("error")}
          options={{ theme: 'dark' }}
        />
      </div>
      <div style={{ textAlign: 'center', marginTop: '2rem', color: '#31EC56', fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}>
        <a href="mailto:info@loxpro.cz" style={{ color: '#31EC56', textDecoration: 'underline' }}>{t.orEmail}</a>
      </div>
      <div
        style={{
          background: '#181A1B',
          color: theme.colors.white,
          margin: '2.5rem auto 0',
          maxWidth: 'min(420px, calc(100vw - 2rem))',
          width: '100%',
          borderRadius: 10,
          padding: 'clamp(1rem, 4vw, 1.5rem) clamp(1rem, 4vw, 2rem)',
          fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)',
          boxShadow: '0 2px 12px rgba(24,26,27,0.06)',
          border: '1px solid #23282F',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ color: theme.colors.green, fontWeight: 600, marginBottom: '1rem' }}>{t.company.header}</div>
        <div style={{ marginBottom: '0.6rem' }}>{t.company.name}</div>
        <div>{t.company.address}</div>
        <div>{t.company.city}</div>
        <div style={{ marginBottom: '0.6rem' }}>{t.company.country}</div>
        <div style={{ marginBottom: '0.6rem' }}>{t.company.phone}</div>
        <div>{t.company.ico}</div>
        <div>{t.company.dic}</div>
      </div>
    </Section>
  );
};

export default Contact;
